import { useState, useSyncExternalStore } from 'react'
import { useApi } from '../../api/context'
import { Icon } from '../../ui/Icon'
import { failureText } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useStaffTest } from './api'
import { staffTestResult, type StaffTestResult } from './model'
import { enablePush, pushStatus, subscribePush, unregisterPush, type PushStatus } from './push'

export interface PushCardProps {
  onToast: (message: string) => void
}

/** Строка под «Push в этом браузере»: в каком он состоянии. */
const STATE: Record<PushStatus, string> = {
  enabled: 'Включены',
  off: 'Выключены',
  denied: 'Запрещены в браузере',
  unsupported: 'Браузер не поддерживает',
  unconfigured: 'Не настроены в этой сборке',
}

/** Что делать, когда переключатель не помогает. */
const HINT: Partial<Record<PushStatus, string>> = {
  denied:
    'Разрешите уведомления в настройках сайта: значок слева от адреса → «Уведомления» → «Разрешить», затем обновите страницу.',
  unsupported: 'Откройте панель в Chrome, Edge или Firefox на компьютере.',
}

/**
 * Настройки уведомлений — содержимое окна по шестерёнке. Две строки:
 * переключатель push в этом браузере (окно разрешения браузер показывает
 * только на нажатие, поэтому включаем по нему) и проверка — тестовое
 * уведомление себе, в колокольчик и в браузер.
 */
export function PushCard({ onToast }: PushCardProps) {
  const api = useApi()
  const { authApi, accessToken } = useSession()
  const status = useSyncExternalStore(subscribePush, pushStatus)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<StaffTestResult | null>(null)
  const test = useStaffTest()
  const hint = HINT[status]
  // Переключатель живой только там, где его нажатие что-то меняет.
  const switchable = status === 'off' || status === 'enabled'

  function toggle(on: boolean) {
    setBusy(true)
    const done = on
      ? enablePush(api, true).then((next) => {
          if (next === 'enabled') onToast('Уведомления в браузере включены')
        })
      : unregisterPush(authApi, accessToken() ?? '').then(() => {
          onToast('Уведомления в браузере выключены')
        })
    done
      .catch((error: unknown) => {
        onToast(`Не удалось включить уведомления. ${failureText(error)}`)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  function check() {
    setResult(null)
    test.mutate(undefined, {
      onSuccess: (dto) => {
        setResult(staffTestResult(dto))
      },
      onError: (error) => {
        setResult({ tone: 'danger', text: failureText(error) })
      },
    })
  }

  return (
    <div className="nt-set">
      <div className="nt-set-row">
        <div className="nt-set-txt">
          <b id="nt-push-title">Push в этом браузере</b>
          <span>{STATE[status]}</span>
        </div>
        <label className="ui-switch">
          <input
            type="checkbox"
            role="switch"
            aria-labelledby="nt-push-title"
            checked={status === 'enabled'}
            disabled={!switchable || busy}
            aria-busy={busy}
            onChange={(e) => {
              toggle(e.target.checked)
            }}
          />
        </label>
      </div>
      {hint ? <p className="nt-set-hint">{hint}</p> : null}

      <div className="nt-set-row">
        <div className="nt-set-txt">
          <b>Тестовое уведомление</b>
          <span>Только вам — в колокольчик и в браузер</span>
        </div>
        <button
          type="button"
          className="ui-btn"
          data-variant="secondary"
          disabled={test.isPending}
          aria-busy={test.isPending}
          onClick={check}
        >
          Отправить
        </button>
      </div>
      {result ? (
        <div className="ui-alert" data-tone={result.tone} role="status">
          <Icon name={result.tone === 'info' ? 'i-info' : 'i-alert'} />
          <div className="txt">{result.text}</div>
        </div>
      ) : null}
    </div>
  )
}
